import { DriveStep } from 'driver.js';
import { ApiMenu } from '../features/sharedComponents/MenuContext';
import { TOUR_DESCRIPTIONS } from './tourDescriptions';

interface User {
  name: string;
  displayRole: string;
  role?: string;
}

const routeToId = (route: string) => `tour-menu-${route.replace(/\//g, '-')}`;

const ensureGroupOpen = (group: string, menus: ApiMenu[]) => {
  const groupMenus = menus.filter((m) => m.group.toLowerCase() === group.toLowerCase());
  if (groupMenus.length === 0) return;
  const firstMenuId = routeToId(groupMenus[0].route);
  if (!document.getElementById(firstMenuId)) {
    const groupBtn = document.getElementById(`tour-group-${group.toLowerCase()}`);
    groupBtn?.click();
  }
};

const ensureUserMenuOpen = () => {
  if (!document.getElementById('tour-header-profile')) {
    document.getElementById('tour-header-welcome')?.click();
  }
};

export const buildAdminSteps = (user: User, menus: ApiMenu[]): DriveStep[] => {
  const steps: DriveStep[] = [
    {
      element: '#tour-header-welcome',
      popover: {
        title: 'Welcome to TETFund TAG 👋',
        description: `Hi ${user.name.trim() || 'User'}! This is your management dashboard. Let's take a quick tour of your available tools.`,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#tour-sidebar',
      popover: {
        title: 'Your Management Sidebar',
        description: "All your available features are grouped here. You'll see groups of tools based on the permissions assigned to you.",
        side: 'right',
      },
    },
    {
      element: '#tour-nav-dashboard',
      popover: {
        title: 'Dashboard Home',
        description: 'Access key performance indicators, summary statistics, and active cycles directly from the main dashboard screen.',
        side: 'right',
      },
    },
  ];

  const groups = ['Settings', 'Users', 'Assignments', 'Submissions', 'Reports', 'Assessments'];
  const userGroups = groups.filter((group) =>
    menus.some((menu) => menu.group.toLowerCase() === group.toLowerCase())
  );

  userGroups.forEach((group) => {
    const groupKey = `group-${group.toLowerCase()}`;
    const groupDesc = TOUR_DESCRIPTIONS[groupKey] || `Access and manage your ${group} menus.`;

    steps.push({
      element: `#tour-group-${group.toLowerCase()}`,
      popover: {
        title: `${group} Tools`,
        description: groupDesc,
        side: 'right',
        onNextClick: (_el, _step, { driver: d }) => {
          ensureGroupOpen(group, menus);
          // Give React a tick to render the expanded child links before driver advances
          setTimeout(() => d.moveNext(), 60);
        },
      },
    });

    const groupMenus = menus
      .filter((m) => m.group.toLowerCase() === group.toLowerCase())
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    groupMenus.forEach((menu) => {
      const desc =
        TOUR_DESCRIPTIONS[menu.route] || `Open ${menu.displayName} from the ${group} group.`;
      steps.push({
        element: `#${routeToId(menu.route)}`,
        popover: {
          title: menu.displayName,
          description: desc,
          side: 'right',
          onPrevClick: (_el, _step, { driver: d }) => {
            ensureGroupOpen(group, menus);
            setTimeout(() => d.movePrevious(), 60);
          },
        },
      });
    });
  });

  steps.push({
    element: '#tour-header-bell',
    popover: {
      title: 'System Notifications',
      description: 'View real-time updates and system notifications relevant to your role.',
      side: 'bottom',
    },
  });

  steps.push({
    element: '#tour-header-guide',
    popover: {
      title: 'Tour Guide Trigger',
      description: 'You can re-trigger this guided tour anytime by clicking the help icon in the header.',
      side: 'bottom',
      onNextClick: (_el, _step, { driver: d }) => {
        ensureUserMenuOpen();
        // Give React a tick to render the dropdown contents before driver advances.
        setTimeout(() => d.moveNext(), 60);
      },
    },
  });

  // User menu items inside the avatar dropdown
  const profileKey = user.role === 'accessor' ? 'profile-reviewer' : 'profile';
  steps.push({
    element: '#tour-header-profile',
    popover: {
      title: 'View Profile',
      description: TOUR_DESCRIPTIONS[profileKey],
      side: 'left',
    },
    onHighlightStarted: () => ensureUserMenuOpen(),
  });

  steps.push({
    element: '#tour-header-change-password',
    popover: {
      title: 'Change Password',
      description: TOUR_DESCRIPTIONS['change-password'],
      side: 'left',
    },
    onHighlightStarted: () => ensureUserMenuOpen(),
  });

  steps.push({
    element: '#tour-header-signout',
    popover: {
      title: 'Sign Out',
      description: TOUR_DESCRIPTIONS['sign-out'],
      side: 'left',
    },
    onHighlightStarted: () => ensureUserMenuOpen(),
  });

  return steps;
};
